import React, { useState, useEffect, useRef } from "react";
import { FiMail, FiEye } from "react-icons/fi";
import { GoTrash } from "react-icons/go";
import Pagination from "../../../components/ui/Pagination";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import PageHeader from "../../../components/ui/PageHeader";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import ViewModal from "../../../components/ui/ViewModal";
import { strings } from "../../../strings/strings";
import { apiCall } from "../../../api/fetchClient";
import { GET_CONTACT_REQUESTS_URL, DELETE_CONTACT_REQUEST_URL, GET_CONTACT_DETAILS_URL, UPDATE_CONTACT_STATUS_URL } from "../../../api/url";
import ContactRequestFilters from "./ContactRequestFilters";
import { getContactViewModalSections } from "./contactUtils";

const ContactRequestPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    request: null,
  });
  const [viewModal, setViewModal] = useState({
    isOpen: false,
    request: null,
    loading: false,
  });

  const searchDebounceRef = useRef(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortOrder: sortBy,
      });

      if (searchQuery) {
        params.append("search", searchQuery);
      }

      const response = await apiCall(
        `${GET_CONTACT_REQUESTS_URL}?${params.toString()}`,
        "GET"
      );

      if (response.success) {
        setRequests(response?.results?.data || []);
        setPagination({
          totalPages: response?.results?.pagination?.totalPages || 1,
          totalItems: response?.results?.pagination?.totalItems || 0,
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const delay = searchQuery ? 500 : 0;
    searchDebounceRef.current = setTimeout(() => {
      fetchRequests();
    }, delay);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [currentPage, sortBy, searchQuery, itemsPerPage]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, itemsPerPage]);

  const handleDelete = async () => {
    try {
      const response = await apiCall(
        `${DELETE_CONTACT_REQUEST_URL}${confirmModal.request._id}`,
        "DELETE"
      );

      if (response.success) {
        fetchRequests();
        window.dispatchEvent(new Event('contactRequestUpdated'));
        closeConfirmModal();
      }
    } catch (error) {
      closeConfirmModal();
    }
  };

  const handleStatusChange = async (request) => {
    if (request.status !== 'pending') return;

    try {
      const response = await apiCall(
        `${UPDATE_CONTACT_STATUS_URL}${request._id}`,
        "PUT",
        { status: 'resolved' }
      );

      if (response.success) {
        fetchRequests();
        window.dispatchEvent(new Event('contactRequestUpdated'));
        // Update view modal state if it's open for this request
        if (viewModal.isOpen && viewModal.request?._id === request._id) {
          setViewModal(prev => ({
            ...prev,
            request: { ...prev.request, status: 'resolved' }
          }));
        }
      }
    } catch (error) {
    }
  };

  const openViewModal = async (request) => {
    try {
      setViewModal({ isOpen: true, request: null, loading: true });
      const response = await apiCall(
        `${GET_CONTACT_DETAILS_URL}${request._id}`,
        "GET"
      );

      if (response.success) {
        setViewModal({
          isOpen: true,
          request: response.results,
          loading: false,
        });
      }
    } catch (error) {
      setViewModal({ isOpen: false, request: null, loading: false });
    }
  };

  const closeViewModal = () => setViewModal({ isOpen: false, request: null, loading: false });

  const openConfirmModal = (request) => {
    setConfirmModal({ isOpen: true, request });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, request: null });
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title={strings.CONTACT_REQUEST_MANAGEMENT}
        description={strings.CONTACT_REQUEST_DESCRIPTION}
        icon={FiMail}
      />

      <ContactRequestFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        sortBy={sortBy}
        setSortBy={setSortBy}
        totalRequests={pagination.totalItems}
      />

      {loading ? (
        <LoadingSpinner message="Loading contact requests..." minHeight="h-64" />
      ) : requests.length > 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-3xl border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 dark:bg-slate-800/50 border-b border-gray-100 dark:border-slate-800">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {strings.CONTACT_NAME}
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {strings.CONTACT_EMAIL}
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">
                    {strings.CONTACT_PHONE}
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {strings.CONTACT_COMPANY}
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {strings.CONTACT_DATE}
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">
                    Status
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                {requests.map((request) => (
                  <tr
                    key={request._id}
                    className="hover:bg-gray-50/50 dark:hover:bg-slate-800/30 transition-colors group"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                   
                        <span className="block text-sm font-bold text-gray-900 dark:text-white group-hover:text-primary transition-colors">
                          {request.firstName} {request.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-300 font-medium lowercase">
                        {request.businessEmail}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                        {request.mobileNumber && request.mobileCode
                          ? `${request.mobileCode} ${request.mobileNumber}`
                          : "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                        {request.companyName || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-bold text-gray-700 dark:text-gray-200">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </span>
                        <span>{new Date(request.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-xs">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider transition-all duration-300 ${
                          request.status === 'pending'
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        }`}
                      >
                        {request.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openViewModal(request)}
                          className="p-2 text-gray-400 hover:text-primary hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-transparent hover:border-gray-100 dark:hover:border-slate-600"
                        >
                          <FiEye className="w-4.5 h-4.5" />
                        </button>
                        <button
                          onClick={() => openConfirmModal(request)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-transparent hover:border-gray-100 dark:hover:border-slate-600"
                        >
                          <GoTrash className="w-4.5 h-4.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 bg-white dark:bg-slate-900 rounded-3xl border border-dashed border-gray-200 dark:border-slate-800">
          <div className="w-16 h-16 bg-gray-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <FiMail className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {strings.NO_CONTACT_REQUESTS}
          </h3>
          {searchQuery && (
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              No results found for "{searchQuery}"
            </p>
          )}
        </div>
      )}

      {pagination.totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          onPageChange={setCurrentPage}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(newLimit) => {
            setItemsPerPage(newLimit);
            setCurrentPage(1);
          }}
          itemsPerPageOptions={[10, 20, 50, 100]}
        />
      )}

      <ViewModal
        isOpen={viewModal.isOpen}
        onClose={closeViewModal}
        title={viewModal.request ? `${viewModal.request.firstName} ${viewModal.request.lastName}` : strings.CONTACT_DETAILS}
        icon={FiMail}
        sections={getContactViewModalSections(viewModal.request)}
        footerActions={
          viewModal.request?.status === 'pending' && (
            <button
              onClick={() => handleStatusChange(viewModal.request)}
              className="px-4 sm:px-6 py-2 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg active:scale-95 text-sm sm:text-base flex items-center gap-2"
            >
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              {strings.CONTACT_MARK_RESOLVED}
            </button>
          )
        }
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={handleDelete}
        title={strings.DELETE_CONTACT_TITLE}
        message={strings.DELETE_CONTACT_MESSAGE(confirmModal.request ? `${confirmModal.request.firstName} ${confirmModal.request.lastName}` : "")}
        confirmText={strings.DELETE}
        variant="danger"
        icon={GoTrash}
      />
    </div>
  );
};

export default ContactRequestPage;
